<script>
	import { fly, fade } from "svelte/transition";
	import { voucherValues, minVoucherValue, lang } from "../store";
	import { formatCurrency, getValues } from "../lib/utils";
	import { TRANSITION_DELAY } from "../constants/constants";

	$: lowestVoucherValue = Math.max(
		$minVoucherValue,
		Math.min(...getValues($voucherValues))
	);

	$: highestVoucherValue = Math.max(
		$minVoucherValue,
		Math.max(...getValues($voucherValues))
	);

	$: voucherRange =
		lowestVoucherValue === highestVoucherValue
			? formatCurrency(lowestVoucherValue, $lang)
			: `${formatCurrency(lowestVoucherValue, $lang)} - ${formatCurrency(
					highestVoucherValue,
					$lang
			  )}`;

	$: isTableVisible = false;
</script>

<div class="psl-container">
	<div class="psl-value-range-and-button">
		<div class="psl-value-range">
			<svg
				class="psl-money-icon"
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 576 512"
			>
				<path
					d="M251.1 207.1C251.1 196.1 260.1 187.1 271.1 187.1H287.1C299 187.1 308 196.1 308 207.1V275.1H312C323 275.1 332 284.1 332 295.1C332 307 323 315.1 312 315.1H263.1C252.1 315.1 243.1 307 243.1 295.1C243.1 284.1 252.1 275.1 263.1 275.1H267.1V227.6C258.9 225.7 251.1 217.7 251.1 207.1zM48.66 79.13C128.4 100.9 208.2 80.59 288 60.25C375 38.08 462 15.9 549 48.38C565.9 54.69 576 71.62 576 89.66V399.5C576 423.4 550.4 439.2 527.3 432.9C447.6 411.1 367.8 431.4 288 451.7C200.1 473.9 113.1 496.1 26.97 463.6C10.06 457.3 0 440.4 0 422.3V112.5C0 88.59 25.61 72.83 48.66 79.13L48.66 79.13zM127.1 416C127.1 380.7 99.35 352 63.1 352V416H127.1zM63.1 223.1C99.35 223.1 127.1 195.3 127.1 159.1H63.1V223.1zM512 352V287.1C476.7 287.1 448 316.7 448 352H512zM512 95.1H448C448 131.3 476.7 159.1 512 159.1V95.1zM287.1 143.1C234.1 143.1 191.1 194.1 191.1 255.1C191.1 317.9 234.1 368 287.1 368C341 368 384 317.9 384 255.1C384 194.1 341 143.1 287.1 143.1z"
				/>
			</svg>
			<p>
				Palvelusetelin arvo tunnilta: <span class="value">
					{voucherRange}
				</span>
			</p>
		</div>

		<button on:click={() => (isTableVisible = !isTableVisible)}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				height="1.1rem"
				viewBox="0 0 448 512"
				><path
					fill="currentColor"
					d={isTableVisible
						? "M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm88 200H296c13.3 0 24 10.7 24 24s-10.7 24-24 24H152c-13.3 0-24-10.7-24-24s10.7-24 24-24z"
						: "M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zM200 344V280H136c-13.3 0-24-10.7-24-24s10.7-24 24-24h64V168c0-13.3 10.7-24 24-24s24 10.7 24 24v64h64c13.3 0 24 10.7 24 24s-10.7 24-24 24H248v64c0 13.3-10.7 24-24 24s-24-10.7-24-24z"}
				/></svg
			>

			{#if isTableVisible}
				Piilota erittely
			{:else if !isTableVisible}
				Näytä erittely
			{/if}
		</button>
	</div>

	{#if isTableVisible}
		<table in:fade>
			<thead>
				<tr>
					<th />
					<th>Klo</th>
					<th>Arvo</th>
				</tr>
			</thead>
			<tr
				in:fly={{
					y: -10,
				}}
			>
				<td>Arkipäivä</td>
				<td>7-18</td>
				<td class="value value--small">
					{formatCurrency($voucherValues["Arkisin"]["7-18"], $lang)}
				</td>
			</tr>
			<tr
				in:fly={{
					y: -10,
					delay: TRANSITION_DELAY * 2,
				}}
			>
				<td>Arkipäivä</td>
				<td>18-22</td>
				<td class="value value--small">
					{formatCurrency($voucherValues["Arkisin"]["18-22"], $lang)}
				</td>
			</tr>
			<tr
				in:fly={{
					y: -10,
					delay: TRANSITION_DELAY * 3,
				}}
			>
				<td> Lauantai</td>
				<td>7-18</td>
				<td class="value value--small">
					{formatCurrency($voucherValues["Lauantai"]["7-18"], $lang)}
				</td>
			</tr>
			<tr
				in:fly={{
					y: -10,
					delay: TRANSITION_DELAY * 4,
				}}
			>
				<td> Lauantai</td>
				<td>18-22</td>
				<td class="value value--small">
					{formatCurrency($voucherValues["Lauantai"]["18-22"], $lang)}
				</td>
			</tr>
			<tr
				in:fly={{
					y: -10,
					delay: TRANSITION_DELAY * 5,
				}}
			>
				<td> Sunnuntai</td>
				<td>7-18</td>
				<td class="value value--small">
					{formatCurrency($voucherValues["Sunnuntai"]["7-18"], $lang)}
				</td>
			</tr>
			<tr
				in:fly={{
					y: -10,
					delay: TRANSITION_DELAY * 6,
				}}
			>
				<td> Sunnuntai</td>
				<td>18-22</td>
				<td class="value value--small">
					{formatCurrency($voucherValues["Sunnuntai"]["18-22"], $lang)}
				</td>
			</tr>
		</table>
	{/if}
</div>

<style>
	.psl-container {
		display: flex;
		flex-direction: column;
		justify-content: center;
		gap: 1rem;
		margin-top: 1rem;
		padding: 1rem;
		background-color: var(--color-blue);
		color: var(--color-gray);
		border-radius: var(--border-radius-small);
		line-height: 1.3;
	}

	.psl-value-range-and-button {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		gap: 1rem;
	}

	@media (min-width: 600px) {
		.psl-value-range-and-button {
			flex-direction: row;
			gap: 2rem;
		}
	}

	.psl-money-icon {
		width: 61px;
		height: 54px;
		fill: var(--color-gray);
		opacity: 0.5;
	}

	.psl-value-range {
		display: flex;
		gap: 1rem;
	}

	p {
		margin: 0;
		display: flex;
		flex-direction: column;
	}

	button {
		display: flex;
		align-items: center;
		font-size: 1rem;
		gap: 0.5rem;
		appearance: none;
		border: 0;
		padding: 0;
		background: none;
		color: var(--color-yellow);
		font-family: inherit;
		opacity: 0.8;
		transition: opacity 0.25s;
	}

	button:hover {
		cursor: pointer;
		opacity: 1;
	}

	table {
		margin: 0 auto;
		border-collapse: collapse;
	}

	th,
	td {
		padding: 0.2rem 1.25rem;
	}

	th {
		text-align: left;
		border-bottom: 1px solid var(--color-cyan);
	}

	.value {
		font-size: 2rem;
		color: var(--color-yellow);
		font-weight: 500;
	}

	.value--small {
		font-size: 1rem;
	}
</style>
